// sign_tbt_attendance (the attendee themself; versionless; CORE-060/061). Shows what
// the talk covered and the work scope, then the attendee's own declaration.
import { useState } from 'react'
import { Banner } from '@/components/ui'
import type { ActionPanelProps } from '@/features/contracts'
import { fmtDateTime } from '@/lib/format'
import { PermitWorkSummary, TalkContent } from '../briefing'
import { PanelFrame } from '../frame'
import { usePanelCommand } from '../hooks'
import { actionArg, revisionOf, talkForAttendee } from '../logic'
import { CycleHeader, roleLabel, SigningStatement, TickRow } from '../parts'

export function TbtAttendancePanel({ bundle, action, onDone, onCancel }: ActionPanelProps) {
  const viewer = bundle.viewer.person
  const found = talkForAttendee(bundle, viewer.id, actionArg(action, 'talk_id'))
  const run = usePanelCommand('sign_tbt_attendance', bundle, onDone)
  const [confirmed, setConfirmed] = useState(false)
  const [tried, setTried] = useState(false)
  const revision = found ? revisionOf(bundle, found.cycle) : null
  const alreadySigned = found?.attendee?.signed ?? null

  const problems: string[] = []
  if (!found) problems.push('There is no toolbox talk for you to sign.')
  if (!confirmed) problems.push('Tick the box to confirm the statement is true for you.')

  function submit() {
    setTried(true)
    if (!found || problems.length > 0) return
    void run.submit({ permit_id: bundle.permit.id, talk_id: found.talk.id })
  }

  return (
    <PanelFrame
      action={action}
      run={run}
      problems={problems}
      tried={tried}
      onCancel={onCancel}
      onSubmit={submit}
      submitLabel="Sign my understanding"
      blockedNote={!found ? 'No toolbox talk to sign.' : alreadySigned ? 'You have already signed this talk.' : null}
    >
      {!found ? (
        <Banner tone="warn" title="No toolbox talk found" className="mb-4">
          There is no toolbox talk on the current cycle waiting for your signature. Close this and refresh the permit.
        </Banner>
      ) : (
        <>
          <CycleHeader cycle={found.cycle} />
          {alreadySigned ? (
            <Banner tone="ok" title="You have already signed this talk" className="mb-4">
              Signed {fmtDateTime(alreadySigned.server_signed_at)}.
            </Banner>
          ) : null}
          {!found.attendee ? (
            <Banner tone="warn" title="You are not on this talk's attendee list" className="mb-4">
              Only the people the PH or PA listed can sign. If you attended, ask them to record a talk that lists you.
            </Banner>
          ) : null}
          <div className="mb-4 rounded-lg border border-line bg-white p-3">
            <p className="mb-3 font-semibold text-ink">
              Toolbox talk given by {found.talk.giver.signer.display_name} ({roleLabel(found.talk.giver_role)}),{' '}
              {fmtDateTime(found.talk.reported_conducted_at)}
            </p>
            <TalkContent talk={found.talk} />
          </div>
          <PermitWorkSummary revision={revision} />
          <SigningStatement role="CREW" person={viewer}>
            <p>I attended this toolbox talk.</p>
            <p>I understand the work and where it stops, the hazards and their controls, and the dynamic risk plan.</p>
            <p>I had the chance to ask questions, and they were answered.</p>
            <p>I will stop work and tell the Permit Holder if anything changes or I am unsure.</p>
          </SigningStatement>
          <div className="mb-4 rounded-lg border border-line bg-white">
            <TickRow checked={confirmed} onChange={setConfirmed} label="This statement is true for me." />
          </div>
        </>
      )}
    </PanelFrame>
  )
}
